// Import project (SPEC F7), on Beat › Projects: pick a .json or .zip from Export project. The import code
// loads only when a file is picked (CLAUDE.md lazy rule). A file that can't be imported says why, inline,
// and changes nothing.
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppStore } from '../../app/store';
import { Progress } from '../../ui/States';
import { useProjects } from './projectsStore';
import styles from './Beat.module.css';

export function useProjectImport() {
  const navigate = useNavigate();
  const showToast = useAppStore((s) => s.showToast);
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController>(undefined);
  const analysisRef = useRef<{ terminate: () => void }>(undefined);
  const [busy, setBusy] = useState<{ done: number; total: number }>();
  const [problem, setProblem] = useState<string>();

  const run = async (file: File) => {
    setProblem(undefined);
    setBusy({ done: 0, total: 1 });
    abort.current = new AbortController();
    try {
      const [{ importProject, importSummary }, repo, samples, engine, { AnalysisClient }] = await Promise.all([
        import('../../export/projectImport'),
        import('../../db/repos/projectIo'),
        import('../../db/repos/samples'),
        import('../../audio/engine'),
        import('../../analysis/client'),
      ]);
      const analysis = new AnalysisClient();
      analysisRef.current = analysis;
      const res = await importProject(
        { name: file.name, bytes: () => file.arrayBuffer() },
        { analysis, browserDecode: engine.browserDecode, sha256Hex: samples.sha256Hex, findByHash: samples.findByHash, now: Date.now, write: repo.writeProjectBundle },
        (p) => setBusy(p),
        abort.current.signal,
      );
      if (!res.ok) {
        if (!res.cancelled) setProblem(res.message);
        return;
      }
      await useProjects.getState().refresh();
      showToast({ message: importSummary(res) });
      navigate(`/beat/${res.result.project.id}`);
    } finally {
      analysisRef.current?.terminate();
      analysisRef.current = undefined;
      abort.current = undefined;
      setBusy(undefined);
    }
  };

  const pick = () => input.current?.click();
  const field = (
    <input
      ref={input}
      type="file"
      accept=".json,.zip,application/json,application/zip"
      className="visually-hidden"
      aria-label="Import project file"
      data-testid="import-project-file"
      tabIndex={-1}
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (f) void run(f);
      }}
    />
  );
  const panel = (
    <>
      {problem && (
        <div className={styles.problem} role="alert">
          <p className="t-label">This file can't be imported</p>
          <p className="t-body">{problem}</p>
        </div>
      )}
      {busy && (
        <Progress
          active
          label="Importing project"
          value={busy.total ? busy.done / busy.total : undefined}
          onCancel={() => abort.current?.abort()}
        />
      )}
    </>
  );
  return { pick, field, panel, busy: Boolean(busy) };
}
